import type { ImportSummary, ListResponse } from '@shared/schemas';
import { all, call, getContext, put, takeLatest, takeLeading } from 'redux-saga/effects';

import type { ImportsGateway } from '../infrastructure/imports.gateway';
import { csmMessageSubmitted, importsRequested } from './imports.actions';
import { importsActions } from './imports.slice';

const DEFAULT_ERROR = 'Failed to load imports.';
const DEFAULT_SEND_ERROR = 'Your message could not be sent. Please try again.';

/** Best-effort human message for the failure banner (no domain error union needed). */
function toMessage(e: unknown, fallback = DEFAULT_ERROR): string {
  if (e instanceof TypeError) {
    return 'Network error; please check your connection and retry.';
  }
  return e instanceof Error && e.message ? e.message : fallback;
}

/**
 * WORKER: fetch the client imports listing and its summary (#695), together:
 * the tiles need both, so either failing fails the load. All I/O via the
 * injected gateway.
 */
function* fetchImportsWorker() {
  const gateway: ImportsGateway = yield getContext('importsGateway');
  yield put(importsActions.loadStarted());
  try {
    const [rows, summary] = (yield all([call(gateway.fetchLists), call(gateway.fetchSummary)])) as [
      ListResponse[],
      ImportSummary,
    ];
    yield put(importsActions.loadSucceeded({ rows, summary }));
  } catch (e) {
    yield put(importsActions.loadFailed(toMessage(e)));
  }
}

/** WORKER: mail the licence's CSM (#695). `takeLeading`: a double click sends once. */
function* sendCsmMessageWorker(action: ReturnType<typeof csmMessageSubmitted>) {
  const gateway: ImportsGateway = yield getContext('importsGateway');
  yield put(importsActions.csmMessageSending());
  try {
    yield call(gateway.sendCsmMessage, action.payload.message);
    yield put(importsActions.csmMessageSent());
  } catch (e) {
    yield put(importsActions.csmMessageFailed(toMessage(e, DEFAULT_SEND_ERROR)));
  }
}

/** WATCHERS. */
export function* importsSaga() {
  yield all([
    takeLatest(importsRequested.type, fetchImportsWorker),
    takeLeading(csmMessageSubmitted.type, sendCsmMessageWorker),
  ]);
}
